import { computed } from 'vue';
import { sectionLabels } from './salesPartyFormOptions';
import {
  salesDeliveryLocation,
  salesReviewLocation,
  shouldReturnToSalesDelivery,
  shouldReturnToSalesReview,
} from './salesReviewNavigation';
import { saveReviewNavigationBeforeSwitch } from './salesReviewNavigationPersistence';

export const salesPartyOriginRequired = ({
  partyId,
  relationType,
  role,
  roleParties,
}) =>
  computed(() => {
    if (role.value !== 'comprador' || relationType.value === 'representante')
      return false;
    if (!roleParties.loaded.value) return false;

    const firstBuyer = roleParties.parties.value[0];
    return partyId.value
      ? String(firstBuyer?.id) === String(partyId.value)
      : !firstBuyer;
  });

const sameId = (left, right) => String(left) === String(right);

export const salesPartyRepresentativeRolesLocked = ({
  parentPartyId,
  partyId,
  relationType,
  roleParties,
}) =>
  computed(() => {
    if (
      relationType.value !== 'representante' ||
      !roleParties?.loaded.value ||
      !parentPartyId.value
    ) {
      return false;
    }

    const company = roleParties.parties.value.find(party =>
      sameId(party.id, parentPartyId.value)
    );
    const representatives = company?.representatives || [];
    if (!company || !representatives.length) return Boolean(company);

    return (
      Boolean(partyId.value) && sameId(representatives[0].id, partyId.value)
    );
  });

export function useSalesPartyRegistrationPage({
  catalog,
  certificateCheck,
  components,
  form,
  partyId,
  partyKind,
  parentPartyId,
  registration,
  relationType,
  roleParties,
  reviewCompletion,
  reviewContext,
  reviewMode,
  role,
  route,
  router,
  internalProfile,
  internalProfileAlert,
  lookupError,
  lookupLoading,
  spouseLookupError,
  spouseLookupLoading,
  t,
  transactionId,
}) {
  const currentComponent = computed(() => components[form.activeSection.value]);
  const representativeRolesLocked = salesPartyRepresentativeRolesLocked({
    parentPartyId,
    partyId,
    relationType,
    roleParties,
  });
  const labels = computed(() => sectionLabels(t));
  const roleLabel = computed(() =>
    role.value === 'vendedor'
      ? t('SALES_PARTIES.ROLE.VENDEDOR_SINGLE')
      : t('SALES_PARTIES.ROLE.COMPRADOR_SINGLE')
  );
  const canEdit = computed(() =>
    Boolean(
      form.party.value?.permissions?.can_edit ??
        registration.transaction.value?.permissions?.can_save_draft
    )
  );
  const pageTitle = computed(() => {
    const editing = Boolean(partyId.value);
    if (relationType.value === 'representante') {
      return editing
        ? t('SALES_PARTIES.REGISTRATION.EDIT_REPRESENTATIVE')
        : t('SALES_PARTIES.REGISTRATION.CREATE_REPRESENTATIVE');
    }
    if (partyKind.value === 'pessoa_juridica') {
      return editing
        ? t('SALES_PARTIES.REGISTRATION.EDIT_COMPANY')
        : t('SALES_PARTIES.REGISTRATION.CREATE_COMPANY');
    }
    return editing
      ? t('SALES_PARTIES.REGISTRATION.EDIT_PERSON')
      : t('SALES_PARTIES.REGISTRATION.CREATE_PERSON');
  });

  const currentBindings = computed(() => {
    if (form.activeSection.value === 'documents') {
      return {
        transactionId: transactionId.value,
        partyId: form.party.value?.id,
        partyKind: form.formParty.value.party_kind,
        partyRole: form.formParty.value.party_role,
        canEdit: canEdit.value,
        documentTypes: catalog.documentTypes.value,
        deliveryCorrection: route.query.return_to === 'delivery',
      };
    }

    const bindings = {
      profile: form.profile.value,
      errors: form.errors.value[form.activeSection.value] || {},
    };
    if (form.activeSection.value === 'address') {
      const transaction = registration.transaction.value || {};
      const property =
        transaction.property?.address || transaction.address || {};
      const profile = transaction.profile || {};
      bindings.canCopyPropertyAddress = role.value === 'vendedor';
      bindings.partyKind = form.formParty.value.party_kind;
      bindings.partyRole = form.formParty.value.party_role;
      bindings.propertyAddress = {
        cep: profile.cep || property.cep || '',
        street:
          profile.logradouro || property.logradouro || property.endereco || '',
        number: profile.numero || '',
        unit: profile.unidade || '',
        complement: profile.complemento || '',
        city: profile.cidade || property.cidade || '',
        state: profile.estado || property.estado || property.uf || '',
        neighborhood: profile.bairro || property.bairro || '',
      };
    }
    if (form.activeSection.value === 'personal') {
      bindings.partyRole = role.value;
      bindings.representative = relationType.value === 'representante';
      bindings.representativeRolesLocked = representativeRolesLocked.value;
      bindings.deliveryCorrection = route.query.return_to === 'delivery';
      bindings.internalProfile = Boolean(internalProfile?.value);
      bindings.internalProfileAlert = Boolean(internalProfileAlert?.value);
      bindings.lookupError = lookupError?.value || '';
      bindings.lookupLoading = Boolean(lookupLoading?.value);
      bindings.spouseLookupError = spouseLookupError?.value || '';
      bindings.spouseLookupLoading = Boolean(spouseLookupLoading?.value);
      bindings.showSpouseCpf =
        form.formParty.value.party_kind === 'pessoa_fisica' &&
        relationType.value !== 'representante' &&
        route.query.return_to !== 'delivery';
    }
    if (form.activeSection.value === 'attorney') {
      bindings.partyRole = role.value;
    }
    if (form.activeSection.value === 'encumbrances') {
      bindings.partyRole = role.value;
    }
    if (form.activeSection.value === 'origin') {
      bindings.originOptions = catalog.originOptions.value;
      bindings.contactOptions = catalog.contactOptions.value;
      bindings.catalogLoading = catalog.loading.value;
    }
    return bindings;
  });

  const goBack = () => {
    if (shouldReturnToSalesReview(route)) {
      return router.push(
        salesReviewLocation({
          accountId: route.params.accountId,
          salesTransactionId: transactionId.value,
        })
      );
    }
    if (shouldReturnToSalesDelivery(route)) {
      return router.push(
        salesDeliveryLocation({
          accountId: route.params.accountId,
          salesTransactionId: transactionId.value,
        })
      );
    }
    return router.push({
      name: 'operacao_imobiliaria_vendas_cadastro_partes',
      params: {
        accountId: route.params.accountId,
        salesTransactionId: transactionId.value,
        partyRole: role.value,
      },
    });
  };

  const ensureRepresentativeRoles = () => {
    if (!representativeRolesLocked.value) return false;

    form.updateField('identity', 'is_partner', true);
    form.updateField('identity', 'is_legal_representative', true);
    return true;
  };
  const completeReviewSection = section => {
    const item = reviewContext?.value?.items?.[0];
    if (!reviewMode?.value || !item || !reviewCompletion?.save) return true;

    return reviewCompletion.save({
      section: item.section,
      targetKey: item.target_key,
      checks: reviewContext.value.sectionChecks?.[section] || [],
    });
  };

  const update = (section, fieldOrValue, value) => {
    if (value === undefined) form.updateSection(section, fieldOrValue);
    else form.updateField(section, fieldOrValue, value);
  };
  const saveAndExit = async () => {
    const section = form.activeSection.value;
    const saved = await form.save();
    if (!saved) return false;
    const completed = await completeReviewSection(section);
    if (completed) await goBack();
    return completed;
  };
  const requestLaborCertificate = section => {
    if (
      section !== 'personal' ||
      form.formParty.value.party_kind !== 'pessoa_fisica' ||
      !form.party.value?.id ||
      !certificateCheck
    ) {
      return;
    }

    Promise.resolve(certificateCheck({ party: form.party.value })).catch(
      () => {}
    );
  };
  const next = async () => {
    const isLast = form.currentIndex.value === form.sections.value.length - 1;
    if (isLast) return saveAndExit();

    const section = form.activeSection.value;
    const advanced = await form.saveAndNext();
    if (advanced) requestLaborCertificate(section);
    if (!advanced || !reviewMode?.value) return advanced;
    if (await completeReviewSection(section)) return true;

    form.activeSection.value = section;
    return false;
  };
  const selectSection = targetSection =>
    saveReviewNavigationBeforeSwitch({
      completeCurrent: completeReviewSection,
      currentSection: form.activeSection.value,
      reviewMode: Boolean(reviewMode?.value),
      saveCurrent: form.save,
      switchTo: section => {
        form.activeSection.value = section;
      },
      targetSection,
    });
  return {
    canEdit,
    currentBindings,
    ensureRepresentativeRoles,
    currentComponent,
    goBack,
    labels,
    next,
    pageTitle,
    roleLabel,
    saveAndExit,
    selectSection,
    update,
  };
}
